import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  LayoutDashboard, User, FileText, Building2, MessageSquare, Briefcase, 
  FileCheck, CreditCard, Bell, Key, Menu, LogOut, Lock, Globe, Shield, Rocket, FolderOpen
} from 'lucide-react';
import ClickProLogo from '../components/ClickProLogo';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { toast } from 'sonner';

import API from '../constants/api'
// API base via getApiBase()

export default function DashboardLayout({ children, flush = false }) {
  const { user, logout, token, hasModule, isProView } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isPro = user?.account_type === 'pro';
  const isProActive = isPro && user?.pro_status === 'actif';
  const isProModeActive = isProView();
  const isKycValidated = user?.kyc_status === 'valide';

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await axios.get(`${API}/notifications?limit=1`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUnreadCount(response.data?.data?.unreadCount ?? 0);
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };
    if (token) fetchUnreadCount();
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Tous les items sont TOUJOURS visibles. Les items "pro" sont verrouillés
  // (grisés + icône cadenas) tant que l'utilisateur n'a pas activé l'espace Pro.
  const navItems = [
    { path: '/tableau-de-bord', label: "Vue d'ensemble", icon: LayoutDashboard },
    { path: '/tableau-de-bord/profil', label: 'Mon profil', icon: User },
    { path: '/tableau-de-bord/messagerie', label: 'Messagerie', icon: MessageSquare },
    { path: '/tableau-de-bord/mes-demandes', label: 'Mes demandes', icon: FileText },
    { path: '/tableau-de-bord/mes-documents', label: 'Mes documents', icon: FolderOpen },
    { path: '/tableau-de-bord/mes-candidatures', label: 'Mes candidatures', icon: FileCheck },
    // Espace Pro - gratuit, activable depuis le profil
    { path: '/tableau-de-bord/ma-page-pro', label: 'Ma One Page', icon: Globe, pro: true },
    { path: '/tableau-de-bord/ma-fiche-artisan', label: 'Ma fiche artisan', icon: Building2, pro: true },
    { path: '/tableau-de-bord/repondre-demandes', label: 'Répondre aux demandes', icon: MessageSquare, pro: true },
    { path: '/tableau-de-bord/kyc', label: 'Badge vérifié', icon: Shield, pro: true },
    // Communs
    { path: '/tableau-de-bord/notifications', label: 'Notifications', icon: Bell, badge: unreadCount },
    { path: '/tableau-de-bord/demandes-acces', label: "Demandes d'accès", icon: Key }
  ];

  const handleLockedClick = (e, item) => {
    e.preventDefault();
    if (item.pro && !isProActive) {
      toast.info("Activez l'espace Pro depuis votre profil pour débloquer cette section.");
      navigate('/tableau-de-bord/profil');
    } else if (item.pro && isProActive && !isProModeActive) {
      toast.info('Passez en mode Professionnel depuis votre profil pour accéder à cette section.');
      navigate('/tableau-de-bord/profil');
    } else if (item.module && !hasModule(item.module)) {
      toast.info("Cette fonctionnalité nécessite une vérification KYC ou un abonnement actif.");
      navigate('/tableau-de-bord/abonnement');
    }
  };

  const Sidebar = ({ mobile = false }) => (
    <div className={`${mobile ? '' : 'hidden md:flex'} flex-col h-full clickpro-sidebar text-white`}>
      <div className="p-4 border-b border-white/10">
        <Link to="/tableau-de-bord" className="flex items-center gap-2">
          <ClickProLogo size="default" />
        </Link>
      </div>

      {/* CTA "Activer l'espace Pro" - GRATUIT - si pas encore pro */}
      {!isProActive && (
        <div className="px-4 pt-4">
          <Link
            to="/tableau-de-bord/profil"
            onClick={() => { if (mobile) setMobileMenuOpen(false); }}
            data-testid="sidebar-activate-pro-cta"
            className="block bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--primary-hover))] hover:opacity-95 rounded-sm p-4 transition-all"
          >
            <div className="flex items-center gap-2 mb-1">
              <Rocket className="w-4 h-4" />
              <span className="font-semibold text-sm tracking-wide">Activer l&apos;espace Pro</span>
            </div>
            <p className="text-xs text-white/85">Gratuit · Immédiat · Sans engagement</p>
          </Link>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto p-4 sidebar-scroll">
        <ul className="space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path
              || (item.path === '/tableau-de-bord/mes-documents' && location.pathname.startsWith('/tableau-de-bord/mes-documents'))
              || (item.path === '/tableau-de-bord/messagerie' && location.pathname.startsWith('/tableau-de-bord/messagerie'));
            const Icon = item.icon;
            // Verrouillé si item Pro et compte non pro, ou compte pro en mode particulier
            const lockedByPro = item.pro && (!isProActive || !isProModeActive);
            // Sinon, verrouillé si un module spécifique est requis et non débloqué (pour les pros)
            const lockedByModule = !lockedByPro && item.module && !hasModule(item.module);
            const isLocked = lockedByPro || lockedByModule;

            return (
              <li key={item.path}>
                <Link
                  to={isLocked ? '#' : item.path}
                  onClick={(e) => {
                    if (isLocked) {
                      handleLockedClick(e, item);
                    }
                    if (mobile) setMobileMenuOpen(false);
                  }}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors text-sm ${
                    isActive 
                      ? 'bg-white text-foreground font-medium shadow-soft' 
                      : isLocked 
                        ? 'text-slate-500 hover:bg-white/5 cursor-not-allowed opacity-70'
                        : 'text-slate-300 hover:bg-white/5 hover:text-white'
                  }`}
                  data-testid={`nav-${item.path.split('/').pop() || 'overview'}${isLocked ? '-locked' : ''}`}
                  title={isLocked ? (lockedByPro ? (isProActive ? "Passez en mode Professionnel depuis votre profil" : "Activez l'espace Pro depuis votre profil") : "Module non débloqué") : undefined}
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  <span className="flex-1 min-w-0 text-sm leading-snug">{item.label}</span>
                  {item.pro && (
                    <Badge className={`text-[10px] px-1.5 ${isLocked ? 'bg-slate-700 text-slate-400' : 'bg-amber-500/20 text-amber-300'}`}>
                      PRO
                    </Badge>
                  )}
                  {isLocked && <Lock className="w-4 h-4" />}
                  {item.badge > 0 && !isLocked && (
                    <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">{item.badge}</span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--primary-hover))] rounded-full flex items-center justify-center font-bold">
            {user?.prenom?.charAt(0) || user?.email?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{user?.prenom} {user?.nom}</p>
            <p className="text-sm text-slate-400 truncate">{user?.email}</p>
          </div>
        </div>
        {isProActive && (
          <div className="mb-3">
            <Badge className={`text-[10px] uppercase tracking-wide ${isKycValidated ? 'bg-[hsl(var(--primary)/0.12)] text-[hsl(var(--primary))]' : 'bg-white/10 text-white/70 border-0'}`}>
              {isKycValidated ? <><Shield className="w-3 h-3 mr-1" /> Pro vérifié</> : <>Pro · Non vérifié</>}
            </Badge>
          </div>
        )}
        <Button variant="ghost" className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800" onClick={handleLogout} data-testid="logout-btn">
          <LogOut className="w-4 h-4 mr-2" />
          Déconnexion
        </Button>
      </div>
    </div>
  );

  return (
    <div className={`bg-background ${flush ? 'h-dvh flex flex-col overflow-hidden' : 'min-h-screen'}`}>
      {/* Mobile Header */}
      <header className="md:hidden bg-white/90 backdrop-blur-md border-b border-border sticky top-0 z-40 shrink-0 shadow-header">
        <div className="flex items-center justify-between h-14 px-4">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <Sidebar mobile />
            </SheetContent>
          </Sheet>
          <ClickProLogo size="default" />
          <Link to="/tableau-de-bord/notifications" className="relative">
            <Bell className="w-6 h-6" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </Link>
        </div>
      </header>

      <div className={`flex w-full ${flush ? 'flex-1 min-h-0' : ''}`}>
        {/* Desktop Sidebar */}
        <div className="hidden md:block w-64 fixed left-0 top-0 h-screen">
          <Sidebar />
        </div>

        {/* Main Content */}
        <main className={`flex-1 w-full min-w-0 md:ml-64 ${flush ? 'p-0 flex flex-col min-h-0 overflow-hidden' : 'p-4 md:p-8'}`}>
          {children}
        </main>
      </div>
    </div>
  );
}
